import type { DenialReasonT } from '@lotushealth/lotushealth_js_sdk/schemas';

/**
 * Refill denial reasons accepted by DoseSpot's ``denyRefill`` endpoint.
 * Aliased from the regenerated SDK so the union always tracks the OpenAPI
 * contract — never hand-extend here, regen the SDK instead.
 */
export type RefillDenialReason = DenialReasonT;

/**
 * Human-readable label for every ``RefillDenialReason``. The ``Record`` shape
 * is exhaustive: adding a reason to the SDK breaks compilation here until a
 * label is supplied, which keeps the UI in lock-step with backend acceptance.
 *
 * Property order matches DoseSpot's canonical enum and is preserved by
 * ``Object.entries`` for downstream ``REFILL_DENIAL_REASON_OPTIONS``.
 */
export const REFILL_DENIAL_REASON_LABELS: Readonly<Record<RefillDenialReason, string>> = {
  DeniedPatientUnknown: 'Patient unknown',
  DeniedPatientNotUnderCare: 'Patient not under care',
  DeniedPatientNoLongerUnderPatientCare: 'Patient no longer under care',
  DeniedTooSoon: 'Too soon to refill',
  DeniedNeverPrescribed: 'Never prescribed',
  DeniedHavePatientContact: 'Have patient contact',
  DeniedRefillInappropriate: 'Refill inappropriate',
  DeniedAlreadyPickedUp: 'Already picked up',
  DeniedAlreadyPickedUpPartialFill: 'Already picked up partial fill',
  DeniedNotPickedUp: 'Not picked up',
  DeniedChangeInappropriate: 'Change inappropriate',
  DeniedNeedAppointment: 'Need appointment',
  DeniedPrescriberNotAssociateWithLocation: 'Prescriber not associated with location',
  DeniedNoPriorAuthAttempt: 'No prior auth attempt',
  DeniedAlreadyHandled: 'Already handled',
  DeniedAtPatientRequest: 'At patient request',
  DeniedPatientAllergicToRequestMed: 'Patient allergic to requested med',
  DeniedMedicationDiscontinued: 'Medication discontinued',
};

export function refillDenialReasonLabel(reason: RefillDenialReason): string {
  return REFILL_DENIAL_REASON_LABELS[reason];
}

export interface RefillDenialReasonOption {
  readonly value: RefillDenialReason;
  readonly label: string;
}

/** Ready-to-use list for ``<Select>``-style UIs, in canonical SDK order. */
export const REFILL_DENIAL_REASON_OPTIONS: ReadonlyArray<RefillDenialReasonOption> = Object.entries(
  REFILL_DENIAL_REASON_LABELS
).map(([value, label]) => ({
  value: value as RefillDenialReason,
  label,
}));

/**
 * Sensible anchor reason for new deny drafts — DoseSpot's existing action
 * wizard already uses this and it never silently auto-completes a destructive
 * action because the user must confirm via the submit button.
 */
export const DEFAULT_REFILL_DENIAL_REASON: RefillDenialReason = 'DeniedRefillInappropriate';
